import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import RefreshIcon from "@mui/icons-material/Refresh";
import commonStore from "../Store/marco/commonStore";
import importJobStore, { parseImportDate } from "./import-job-store";
import { getImportDateLocale } from "./import-date-locale";
import {
  applyModuleNavigation,
  getModuleMeta,
  IMPORT_MODULES,
} from "./import-job-config";
import { dataEntryText, getDataEntryImportLabel } from "./data-entry-excel";
import "./import-job.css";

const PAGE_SIZE = 20;

const formatDate = (value, includeDate = true) => {
  const date = parseImportDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat(
    getImportDateLocale(commonStore.lang),
    includeDate
      ? { dateStyle: "medium", timeStyle: "short" }
      : { timeStyle: "short" },
  ).format(date);
};

const formatElapsed = (value, t) => {
  const date = parseImportDate(value);
  if (!date) return t("just now");

  const elapsedSeconds = Math.max(
    0,
    Math.floor((Date.now() - date.getTime()) / 1000),
  );
  if (elapsedSeconds < 60) return t("just now");

  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) {
    return t("{{count}} min ago", { count: elapsedMinutes });
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return t("{{count}} hr ago", { count: elapsedHours });
  }

  return t("{{count}} days ago", {
    count: Math.floor(elapsedHours / 24),
  });
};

const getVisualStatus = (job) => {
  if (job.status === "processing") return "processing";
  if (job.status === "failed") return "failed";
  if (job.status === "completed" && job.errorRows > 0) return "errors";
  return "completed";
};

const statusColor = {
  processing: "#1a56a8",
  completed: "#1d7a4a",
  errors: "#c0392b",
  failed: "#c0392b",
};

const softModuleColors = {
  EA: { background: "#eaf2ff", color: "#1756a9" },
  DE: { background: "#efedff", color: "#534ab7" },
  EF: { background: "#fbeee9", color: "#993c1d" },
  OL: { background: "#e2f0f1", color: "#0c6674" },
};

const historySelectSx = {
  backgroundColor: "#fff",
  borderRadius: "6px",
  height: 40,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d0d5dd",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#98a2b3",
  },
  "&.Mui-focused": {
    boxShadow: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#98a2b3",
    borderWidth: "1px",
  },
};

const historyMenuProps = {
  PaperProps: {
    sx: {
      border: "1px solid #e4e7ec",
      borderRadius: "6px",
      boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)",
      mt: 0.5,
    },
  },
  MenuListProps: {
    sx: {
      p: 0.5,
      "& .MuiMenuItem-root": {
        borderRadius: "4px",
        fontSize: 13,
        minHeight: 36,
      },
      "& .MuiMenuItem-root.Mui-focusVisible": {
        backgroundColor: "#f2f4f7",
      },
    },
  },
};

const ModulePill = ({ item, soft = false }) => {
  const meta = getModuleMeta(item.module);
  const moduleAbbr = item.moduleAbbr || meta.abbr;
  const softColors = softModuleColors[moduleAbbr];
  return (
    <span
      className={`import-job-module-pill ${soft ? "is-soft" : ""}`}
      style={
        soft
          ? {
              backgroundColor: softColors?.background || "#f2f4f7",
              color: softColors?.color || "#475467",
            }
          : { backgroundColor: meta.color }
      }
    >
      {moduleAbbr}
    </span>
  );
};

const HistoryMeta = observer(({ item, t, includeDate = true }) => {
  const visualStatus = getVisualStatus(item);
  const recordCount = item.totalRows || item.processedRows || 0;
  const rowUnit = item.module === IMPORT_MODULES.DE ? dataEntryText("rows", t, commonStore.lang) : t("records");
  const typeLabel = getDataEntryImportLabel(item.importType, t, commonStore.lang);
  if (visualStatus === "processing") {
    return (
      <Typography color="text.secondary" sx={{ fontSize: 11 }}>
        {typeLabel && `${typeLabel} · `}{t("Processing")} ·{" "}
        {t("Started {{time}}", {
          time: formatElapsed(item.startedAt || item.createdAt, t),
        })}
        {item.totalRows > 0 && (
          <>
            {" "}· {item.processedRows || 0} / {item.totalRows} {rowUnit}
          </>
        )}
        {" "}· {item.progress || 0}%
      </Typography>
    );
  }

  const statusLabel =
    visualStatus === "failed"
        ? t("Failed")
      : visualStatus === "errors"
        ? t("Completed with errors")
        : t("Completed");

  return (
    <Typography color="text.secondary" sx={{ fontSize: 11 }}>
      {typeLabel && `${typeLabel} · `}{statusLabel} · {formatDate(item.completedAt || item.createdAt, includeDate)}
      {recordCount > 0 && (
        <> · {recordCount} {rowUnit}</>
      )}
      {visualStatus !== "processing" && (
        <> · {item.errorRows || 0} {t("errors")}</>
      )}
    </Typography>
  );
});

const AllHistoryDialog = observer(({ onOpenJob }) => {
  const { t } = useTranslation("ActivityMain");
  const [moduleFilter, setModuleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const filteredItems = importJobStore.history.filter((item) => {
    const moduleMatches = !moduleFilter || item.moduleAbbr === moduleFilter;
    const statusMatches =
      !statusFilter || getVisualStatus(item) === statusFilter;
    return moduleMatches && statusMatches;
  });

  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = filteredItems.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const changeFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={importJobStore.allHistoryOpen}
      onClose={() => importJobStore.setAllHistoryOpen(false)}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          maxHeight: "calc(100vh - 48px)",
          maxWidth: 820,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: 3, py: 2 }}>
        <Stack direction="row" alignItems="center">
          <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700, color: "#1d2939" }}>
            {t("All import history")}
          </Typography>
          <Tooltip title={t("Close")}>
            <IconButton
              size="small"
              sx={{ color: "#667085" }}
              onClick={() => importJobStore.setAllHistoryOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{
            alignItems: { xs: "stretch", sm: "center" },
            backgroundColor: "#fafbfc",
            borderBottom: "1px solid #e4e7ec",
            px: 3,
            py: 1.75,
          }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
            <Select
              displayEmpty
              MenuProps={historyMenuProps}
              sx={historySelectSx}
              value={moduleFilter}
              onChange={changeFilter(setModuleFilter)}
            >
              <MenuItem value="">{t("All modules")}</MenuItem>
              <MenuItem value="EA">{t("Emission Activity")}</MenuItem>
              <MenuItem value="DE">{t("Data Entry")}</MenuItem>
              <MenuItem value="EF">{t("Emission Factor")}</MenuItem>
              <MenuItem value="OL">{t("Organization List")}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
            <Select
              displayEmpty
              MenuProps={historyMenuProps}
              sx={historySelectSx}
              value={statusFilter}
              onChange={changeFilter(setStatusFilter)}
            >
              <MenuItem value="">{t("All statuses")}</MenuItem>
              <MenuItem value="processing">{t("Processing")}</MenuItem>
              <MenuItem value="completed">{t("Completed")}</MenuItem>
              <MenuItem value="errors">{t("Completed with errors")}</MenuItem>
              <MenuItem value="failed">{t("Failed")}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <Typography
            color="text.secondary"
            sx={{ alignSelf: { xs: "flex-start", sm: "center" }, fontSize: 12 }}
          >
            {filteredItems.length} {t("records")}
          </Typography>
        </Stack>

        {importJobStore.historyLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={30} />
          </Box>
        ) : pageItems.length ? (
          <Box sx={{ px: 3 }}>
            {pageItems.map((item) => {
              const visualStatus = getVisualStatus(item);
              const canReupload = ["errors", "failed"].includes(visualStatus);
              return (
                <Box
                  key={item.jobId}
                  sx={{
                    alignItems: "center",
                    borderBottom: "1px solid #eaecf0",
                    display: "grid",
                    gap: 1.5,
                    gridTemplateColumns: {
                      xs: "38px minmax(0, 1fr)",
                      sm: "42px minmax(0, 1fr) auto",
                    },
                    minHeight: 72,
                    mx: -1,
                    px: 1,
                    py: 1.25,
                    transition: "background-color 120ms ease",
                    "&:hover": { backgroundColor: "#f8fafc" },
                  }}
                >
                  <ModulePill item={item} />
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {visualStatus === "processing" ? (
                        <CircularProgress
                          size={14}
                          thickness={5}
                          sx={{ color: statusColor.processing }}
                        />
                      ) : (
                        <span
                          className="import-job-history-dot"
                          style={{ background: statusColor[visualStatus] }}
                        />
                      )}
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                        {item.fileName}
                      </Typography>
                    </Stack>
                    <Box sx={{ pl: 2 }}>
                      <HistoryMeta item={item} t={t} />
                    </Box>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      gridColumn: { xs: "1 / -1", sm: "auto" },
                      justifyContent: "flex-end",
                    }}
                  >
                    {visualStatus === "errors" && (
                      <Tooltip title={t("Download report")}>
                        <IconButton
                          size="small"
                          onClick={() => importJobStore.downloadReport(item)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canReupload && (
                      <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        sx={{ fontWeight: 600, textTransform: "none" }}
                        onClick={() => onOpenJob(item, true)}
                      >
                        {t("Re-upload")}
                      </Button>
                    )}
                    <Button
                      size="small"
                      sx={{ fontWeight: 600, minWidth: 56, textTransform: "none" }}
                      onClick={() => onOpenJob(item, false)}
                    >
                      {t("View")}
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ py: 8, textAlign: "center" }}>
            {t("No import history")}
          </Typography>
        )}

        {pageCount > 1 && (
          <Box
            sx={{
              borderTop: "1px solid #e4e7ec",
              display: "flex",
              justifyContent: "center",
              px: 3,
              py: 1.5,
            }}
          >
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_event, nextPage) => setPage(nextPage)}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
});

export const ImportNotifications = observer(() => {
  const { t } = useTranslation("ActivityMain");
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    importJobStore.initialize();
    return () => importJobStore.deactivate();
  }, []);

  const historyItems = importJobStore.todayHistory;
  const active = importJobStore.activeJob;
  const items = active
    ? [active, ...historyItems.filter((item) => item.jobId !== active.jobId)]
    : historyItems;

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
    importJobStore.markNotificationsSeen();
  };

  const openJob = (item, reupload) => {
    setAnchorEl(null);
    importJobStore.setAllHistoryOpen(false);
    applyModuleNavigation(item);
    importJobStore.queueHistoryItem(item, reupload);
    commonStore._handleSetIsShowExcelImportExportModal(true, item.importType);
    navigate(item.module === "organization_list" ? "/organizational-structure" : "/emission-view");
  };

  const badgeColor =
    importJobStore.bellState === "processing" ? "success" : "error";
  const showBadge = importJobStore.bellState !== "empty";

  return (
    <>
      <Tooltip title={t("Import notifications")}>
        <IconButton
          aria-label={t("Import notifications")}
          size="small"
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #d0d5dd",
            borderRadius: "6px",
            color: "#f79009",
            cursor: "pointer",
            flex: "0 0 34px",
            height: 34,
            m: 0,
            p: 0,
            transition: "none",
            width: 34,
            "&:hover": {
              backgroundColor: "#ffffff",
              borderColor: "#d0d5dd",
            },
          }}
          onClick={openMenu}
        >
          <Badge
            color={badgeColor}
            invisible={!showBadge}
            overlap="circular"
            variant="dot"
          >
            <NotificationsIcon sx={{ fontSize: 19 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              border: "1px solid #e4e7ec",
              borderRadius: "8px",
              boxShadow: "0 10px 28px rgba(16, 24, 40, 0.16)",
              mt: 1,
              overflow: "hidden",
              width: 370,
            },
          },
        }}
      >
        <Box sx={{ alignItems: "center", display: "flex", px: 2, py: 1.4 }}>
          <Typography sx={{ color: "#1d2939", flex: 1, fontSize: 13, fontWeight: 700 }}>
            {t("Imports today")}
          </Typography>
          <Button
            size="small"
            startIcon={<NorthEastIcon sx={{ fontSize: "12px !important" }} />}
            sx={{
              fontSize: 12,
              fontWeight: 500,
              minWidth: 0,
              px: 0.5,
              textTransform: "none",
            }}
            onClick={() => {
              setAnchorEl(null);
              importJobStore.setAllHistoryOpen(true);
            }}
          >
            {t("View all history")}
          </Button>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          {importJobStore.historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : items.length ? (
            items.map((item) => {
              const visualStatus = getVisualStatus(item);
              const canReupload = ["errors", "failed"].includes(visualStatus);
              return (
                <Box
                  key={item.jobId}
                  sx={{
                    alignItems: "center",
                    borderBottom: "1px solid #eaecf0",
                    display: "grid",
                    gap: 1.25,
                    gridTemplateColumns: "16px minmax(0, 1fr) auto",
                    minHeight: 62,
                    px: 2,
                    py: 1.15,
                    transition: "background-color 120ms ease",
                    "&:hover": { backgroundColor: "#f8fafc" },
                  }}
                >
                  {visualStatus === "processing" ? (
                    <CircularProgress
                      size={15}
                      thickness={5}
                      sx={{ color: statusColor.processing }}
                    />
                  ) : (
                    <span
                      className="import-job-history-dot"
                      style={{ background: statusColor[visualStatus] }}
                    />
                  )}
                  <Box
                    sx={{ cursor: "pointer", minWidth: 0 }}
                    onClick={() => openJob(item, false)}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography noWrap sx={{ color: "#1d2939", fontSize: 12, fontWeight: 600 }}>
                        {item.fileName}
                      </Typography>
                      <ModulePill item={item} soft />
                    </Stack>
                    <Box sx={{ mt: 0.25 }}>
                      <HistoryMeta item={item} t={t} includeDate={false} />
                    </Box>
                  </Box>
                  {canReupload && (
                    <Button
                      color="error"
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "#f1a7a0",
                        fontSize: 11,
                        minWidth: 72,
                        px: 1,
                        textTransform: "none",
                      }}
                      onClick={() => openJob(item, true)}
                    >
                      {t("Re-upload")}
                    </Button>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center", fontSize: 12 }}>
              {t("No imports today")}
            </Typography>
          )}
        </Box>
      </Menu>

      <AllHistoryDialog onOpenJob={openJob} />
    </>
  );
});

export default ImportNotifications;
